/**
 * EventsAdminDialog — developer-only Event Manager UI.
 *
 * Admins can create/edit/delete events (name + start/end datetime + two
 * "double credits" toggles + a mission whitelist). The client GET
 * /events/active endpoint returns currently-active events; the engine
 * uses that to gate `event_only` missions in the spawn pool, and the
 * backend reward flows apply 2x multipliers when an active event has
 * the corresponding double-credit flag set.
 */
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Calendar, CircleNotch, Plus, TrashSimple, PencilSimple, X, Check } from "@phosphor-icons/react";

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { listEvents, createEvent, updateEvent, deleteEvent, listMissions } from "@/lib/api";

const BLANK = {
  name: "",
  starts_at: "",
  ends_at: "",
  double_purchase_credits: false,
  double_mission_credits: false,
  mission_ids: [],
  enabled: true,
};

/** Convert an ISO string ↔ the "YYYY-MM-DDTHH:mm" value the native
 * datetime-local input expects. Round-tripping through Date keeps
 * timezone-local values intact (we serialize to UTC on submit). */
function isoToLocalInput(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return "";
    const off = d.getTimezoneOffset() * 60_000;
    return new Date(d.getTime() - off).toISOString().slice(0, 16);
  } catch { return ""; }
}
function localInputToIso(v) {
  if (!v) return "";
  try {
    return new Date(v).toISOString();
  } catch { return v; }
}

export default function EventsAdminDialog({ open, onOpenChange }) {
  const [events, setEvents] = useState([]);
  const [missions, setMissions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(null); // event object OR "new"
  const [form, setForm] = useState(BLANK);
  const [saving, setSaving] = useState(false);

  const refresh = async () => {
    setLoading(true);
    try {
      const [e, m] = await Promise.all([listEvents(), listMissions()]);
      setEvents(e.events || []);
      setMissions(m.missions || []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to load events");
    } finally { setLoading(false); }
  };

  useEffect(() => {
    if (open) refresh();
  }, [open]);

  const openNew = () => {
    setEditing("new");
    setForm({ ...BLANK });
  };
  const openEdit = (ev) => {
    setEditing(ev);
    setForm({
      name: ev.name || "",
      starts_at: isoToLocalInput(ev.starts_at),
      ends_at: isoToLocalInput(ev.ends_at),
      double_purchase_credits: !!ev.double_purchase_credits,
      double_mission_credits: !!ev.double_mission_credits,
      mission_ids: [...(ev.mission_ids || [])],
      enabled: ev.enabled !== false,
    });
  };
  const closeForm = () => { setEditing(null); setForm(BLANK); };

  const save = async () => {
    if (!form.name.trim()) { toast.error("Name required"); return; }
    if (!form.starts_at || !form.ends_at) { toast.error("Start + end required"); return; }
    const payload = {
      name: form.name.trim(),
      starts_at: localInputToIso(form.starts_at),
      ends_at: localInputToIso(form.ends_at),
      double_purchase_credits: !!form.double_purchase_credits,
      double_mission_credits: !!form.double_mission_credits,
      mission_ids: form.mission_ids || [],
      enabled: form.enabled !== false,
    };
    setSaving(true);
    try {
      if (editing === "new") await createEvent(payload);
      else await updateEvent(editing.id, payload);
      toast.success("Event saved");
      await refresh();
      closeForm();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  const remove = async (ev) => {
    if (!window.confirm(`Delete event "${ev.name}"?`)) return;
    try {
      await deleteEvent(ev.id);
      toast.success("Event deleted");
      await refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Delete failed");
    }
  };

  const toggleMission = (mid) => {
    setForm((f) => {
      const has = f.mission_ids.includes(mid);
      return { ...f, mission_ids: has ? f.mission_ids.filter((x) => x !== mid) : [...f.mission_ids, mid] };
    });
  };

  const now = Date.now();
  const isActive = (ev) => {
    try {
      const s = new Date(ev.starts_at).getTime();
      const e = new Date(ev.ends_at).getTime();
      return ev.enabled !== false && s <= now && now <= e;
    } catch { return false; }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="events-admin-dialog"
        className="rounded-none border border-amber-500/30 bg-[#0A0E14] text-white max-w-4xl p-0 max-h-[90vh] grid grid-rows-[auto_minmax(0,1fr)] gap-0"
      >
        <DialogHeader className="px-5 py-3 border-b border-amber-500/20 bg-amber-500/[0.04]">
          <DialogTitle className="mono uppercase tracking-widest text-[12px] text-amber-300 flex items-center gap-2">
            <Calendar size={14} weight="fill" /> Event Manager
            <span className="flex-1" />
            {!editing && (
              <Button
                data-testid="event-new-btn"
                size="sm"
                onClick={openNew}
                className="rounded-none h-7 px-2 mono uppercase text-[10px] tracking-widest bg-amber-500 text-black hover:bg-amber-400"
              >
                <Plus size={11} className="mr-1.5" /> New Event
              </Button>
            )}
          </DialogTitle>
        </DialogHeader>

        <div className="overflow-y-auto px-5 py-4">
          {loading ? (
            <div className="flex items-center justify-center py-10 text-zinc-500 mono text-[11px] uppercase">
              <CircleNotch size={16} className="animate-spin mr-2" /> Loading…
            </div>
          ) : editing ? (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <label className="mono text-[10px] uppercase tracking-widest text-zinc-400">
                  Name
                  <input
                    data-testid="event-name-input"
                    className="mt-1 w-full bg-[#0F1115] border border-white/10 rounded-none px-2 py-1 text-[13px] text-zinc-100"
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                  />
                </label>
                <label className="mono text-[10px] uppercase tracking-widest text-zinc-400">
                  Enabled
                  <div className="mt-1">
                    <label className="flex items-center gap-2 text-[12px] normal-case tracking-normal text-zinc-200">
                      <input
                        type="checkbox"
                        data-testid="event-enabled-input"
                        checked={form.enabled}
                        onChange={(e) => setForm({ ...form, enabled: e.target.checked })}
                      />
                      Active window is honored
                    </label>
                  </div>
                </label>
                <label className="mono text-[10px] uppercase tracking-widest text-zinc-400">
                  Starts at
                  <input
                    type="datetime-local"
                    data-testid="event-starts-input"
                    className="mt-1 w-full bg-[#0F1115] border border-white/10 rounded-none px-2 py-1 text-[13px] text-zinc-100"
                    value={form.starts_at}
                    onChange={(e) => setForm({ ...form, starts_at: e.target.value })}
                  />
                </label>
                <label className="mono text-[10px] uppercase tracking-widest text-zinc-400">
                  Ends at
                  <input
                    type="datetime-local"
                    data-testid="event-ends-input"
                    className="mt-1 w-full bg-[#0F1115] border border-white/10 rounded-none px-2 py-1 text-[13px] text-zinc-100"
                    value={form.ends_at}
                    onChange={(e) => setForm({ ...form, ends_at: e.target.value })}
                  />
                </label>
              </div>
              <div className="grid grid-cols-2 gap-3 border border-white/5 bg-white/[0.02] p-3">
                <label className="flex items-center gap-2 text-[12px] text-zinc-200">
                  <input
                    type="checkbox"
                    data-testid="event-double-purchase"
                    checked={form.double_purchase_credits}
                    onChange={(e) => setForm({ ...form, double_purchase_credits: e.target.checked })}
                  />
                  2× credits on gem purchases
                </label>
                <label className="flex items-center gap-2 text-[12px] text-zinc-200">
                  <input
                    type="checkbox"
                    data-testid="event-double-mission"
                    checked={form.double_mission_credits}
                    onChange={(e) => setForm({ ...form, double_mission_credits: e.target.checked })}
                  />
                  2× credits on missions + patient transports
                </label>
              </div>

              <div>
                <div className="mono text-[10px] uppercase tracking-widest text-zinc-400 mb-2">
                  Event Missions — will ONLY spawn during this event
                </div>
                <div className="max-h-64 overflow-y-auto border border-white/10 divide-y divide-white/5">
                  {missions.filter((m) => m.event_only).length === 0 && (
                    <div className="px-3 py-4 mono text-[10px] uppercase tracking-widest text-zinc-500">
                      No missions flagged "event_only". Toggle it on a mission in the Missions CMS first.
                    </div>
                  )}
                  {missions.filter((m) => m.event_only).map((m) => (
                    <label
                      key={m.id}
                      data-testid={`event-mission-row-${m.id}`}
                      className="flex items-center gap-2 px-3 py-1.5 text-[11px] cursor-pointer hover:bg-white/5"
                    >
                      <input
                        type="checkbox"
                        data-testid={`event-mission-toggle-${m.id}`}
                        checked={form.mission_ids.includes(m.id)}
                        onChange={() => toggleMission(m.id)}
                      />
                      <span className="mono text-[10px] uppercase tracking-widest text-zinc-300 flex-1">
                        {m.label} ({m.id})
                      </span>
                      <span className="mono text-[9px] uppercase tracking-widest text-zinc-500">
                        {m.service_type}
                      </span>
                    </label>
                  ))}
                </div>
              </div>

              <div className="flex justify-end gap-2 pt-2">
                <Button
                  data-testid="event-cancel-btn"
                  size="sm"
                  variant="outline"
                  onClick={closeForm}
                  className="rounded-none h-8 mono uppercase text-[10px] tracking-widest border-zinc-600 text-zinc-400 hover:bg-zinc-800"
                >
                  <X size={11} className="mr-1.5" /> Cancel
                </Button>
                <Button
                  data-testid="event-save-btn"
                  size="sm"
                  disabled={saving}
                  onClick={save}
                  className="rounded-none h-8 px-3 mono uppercase text-[10px] tracking-widest bg-emerald-500 text-black hover:bg-emerald-400"
                >
                  <Check size={11} className="mr-1.5" /> Save
                </Button>
              </div>
            </div>
          ) : (
            <div className="space-y-2">
              {events.length === 0 && (
                <div className="mono text-[11px] uppercase tracking-widest text-zinc-500 py-10 text-center">
                  No events yet — create one to get started.
                </div>
              )}
              {events.map((ev) => (
                <div
                  key={ev.id}
                  data-testid={`event-row-${ev.id}`}
                  className={`border ${isActive(ev) ? "border-emerald-500/40 bg-emerald-500/[0.05]" : "border-white/10 bg-white/[0.02]"} p-3 flex items-start gap-3`}
                >
                  <div className="flex-1">
                    <div className="mono text-[13px] font-bold text-zinc-100 mb-1">
                      {ev.name}
                      {isActive(ev) && (
                        <span className="ml-2 mono text-[9px] uppercase tracking-widest text-emerald-300 border border-emerald-400/40 px-1.5 py-px">
                          LIVE
                        </span>
                      )}
                      {!ev.enabled && (
                        <span className="ml-2 mono text-[9px] uppercase tracking-widest text-zinc-500">
                          DISABLED
                        </span>
                      )}
                    </div>
                    <div className="mono text-[10px] uppercase tracking-widest text-zinc-500">
                      {new Date(ev.starts_at).toLocaleString()} → {new Date(ev.ends_at).toLocaleString()}
                    </div>
                    <div className="mono text-[10px] uppercase tracking-widest text-zinc-500 mt-1 flex gap-3">
                      {ev.double_purchase_credits && (
                        <span className="text-cyan-300">2× PURCHASES</span>
                      )}
                      {ev.double_mission_credits && (
                        <span className="text-emerald-300">2× MISSIONS</span>
                      )}
                      <span>Missions: {ev.mission_ids?.length || 0}</span>
                    </div>
                  </div>
                  <div className="flex flex-col gap-1">
                    <Button
                      data-testid={`event-edit-${ev.id}`}
                      size="sm"
                      variant="outline"
                      onClick={() => openEdit(ev)}
                      className="rounded-none h-7 px-2 mono uppercase text-[9px] tracking-widest border-sky-400/40 text-sky-300 hover:bg-sky-500/10"
                    >
                      <PencilSimple size={10} className="mr-1" /> Edit
                    </Button>
                    <Button
                      data-testid={`event-delete-${ev.id}`}
                      size="sm"
                      variant="outline"
                      onClick={() => remove(ev)}
                      className="rounded-none h-7 px-2 mono uppercase text-[9px] tracking-widest border-red-400/40 text-red-300 hover:bg-red-500/10"
                    >
                      <TrashSimple size={10} className="mr-1" /> Delete
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
